"use client";
import React, {
  forwardRef,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { OptimizedGlass } from "../../primitives";
import { Motion } from "../../primitives";
import { cn } from "../../lib/utilsComprehensive";
import { useA11yId } from "../../utils/a11y";
import { useMotionPreferenceContext } from "../../contexts/MotionPreferenceContext";
import { useGlassSound } from "../../utils/soundDesign";

export interface HologramLayer {
  id: string;
  type: "image" | "video" | "text" | "shape" | "particle";
  content?: string | React.ReactNode;
  source?: string;
  opacity: number;
  depth: number; // Z-axis position
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  position: { x: number; y: number; z: number };
  animation?: {
    type: "rotate" | "float" | "pulse" | "fade" | "wave" | "spiral";
    speed: number;
    amplitude: number;
    enabled: boolean;
  };
  glitch?: {
    enabled: boolean;
    intensity: number;
    frequency: number;
  };
  holographic?: {
    interference: boolean;
    chromatic: boolean;
    scanlines: boolean;
    noise: number;
  };
}

export interface HologramProjection {
  angle: number; // Viewing angle
  distance: number; // Distance from viewer
  height: number; // Projection height
  tilt: number; // Vertical tilt
}

export interface GlassHologramProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Hologram layers */
  layers?: HologramLayer[];
  /** Projection settings */
  projection?: HologramProjection;
  /** Holographic effects intensity */
  intensity?: "low" | "medium" | "high" | "ultra";
  /** Color scheme */
  colorScheme?:
    | "blue"
    | "green"
    | "red"
    | "purple"
    | "cyan"
    | "white"
    | "rainbow";
  /** Environment lighting */
  ambientLight?: number;
  /** Whether to enable auto-rotation */
  autoRotate?: boolean;
  /** Auto-rotation speed */
  rotationSpeed?: number;
  /** Whether to respond to mouse movement */
  interactive?: boolean;
  /** Hologram size */
  size?: "sm" | "md" | "lg" | "xl";
  /** Background pattern */
  background?: "none" | "grid" | "circuit" | "matrix" | "particles";
  /** Scan line effect */
  scanLines?: boolean;
  /** Glitch effects */
  glitchEnabled?: boolean;
  /** Chromatic aberration */
  chromaticAberration?: boolean;
  /** Noise overlay */
  noiseOverlay?: boolean;
  /** Depth of field blur */
  depthOfField?: boolean;
  /** Layer selection handler */
  onLayerSelect?: (layer: HologramLayer) => void;
  /** Projection change handler */
  onProjectionChange?: (projection: HologramProjection) => void;
  /** Custom shader effects */
  customShaders?: string[];
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string;
  /** Controls overlay */
  showControls?: boolean;
  /** Debug mode */
  debug?: boolean;
  /** Respect motion preferences */
  respectMotionPreference?: boolean;
}

export const GlassHologram = forwardRef<HTMLDivElement, GlassHologramProps>(
  (
    {
      layers = [],
      projection = { angle: 0, distance: 100, height: 200, tilt: 0 },
      intensity = "medium",
      colorScheme = "cyan",
      ambientLight = 0.3,
      autoRotate = false,
      rotationSpeed = 1,
      interactive = true,
      size = "md",
      background = "grid",
      scanLines = true,
      glitchEnabled = false,
      chromaticAberration = true,
      noiseOverlay = true,
      depthOfField = true,
      onLayerSelect,
      onProjectionChange,
      customShaders = [],
      loading = false,
      error,
      showControls = true,
      debug = false,
      respectMotionPreference = true,
      className,
      ...props
    },
    ref
  ) => {
    const { prefersReducedMotion } = useMotionPreferenceContext();
    const { play, feedback } = useGlassSound();
    const hologramId = useA11yId("glass-hologram");

    const [currentProjection, setCurrentProjection] = useState(projection);
    const [selectedLayer, setSelectedLayer] = useState<string | null>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const [animationTime, setAnimationTime] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const hologramRef = useRef<HTMLDivElement>(null);
    const animationFrameRef = useRef<number>();

    // Size configurations
    const sizeConfig = {
      sm: { width: 200, height: 150, scale: 0.8 },
      md: { width: 300, height: 225, scale: 1 },
      lg: { width: 400, height: 300, scale: 1.2 },
      xl: { width: 500, height: 375, scale: 1.5 },
    };

    const config = sizeConfig[size];

    // Color scheme configurations
    const colorSchemes = {
      blue: {
        primary: "hsl(var(--glass-color-primary))",
        secondary: "#1E40AF",
        accent: "var(--glass-color-primary-light)",
      },
      green: {
        primary: "hsl(var(--glass-color-success))",
        secondary: "#047857",
        accent: "var(--glass-color-success-light)",
      },
      red: {
        primary: "hsl(var(--glass-color-danger))",
        secondary: "#B91C1C",
        accent: "var(--glass-color-danger-light)",
      },
      purple: { primary: "#8B5CF6", secondary: "#6D28D9", accent: "#A78BFA" },
      cyan: { primary: "#06B6D4", secondary: "#0891B2", accent: "#22D3EE" },
      white: {
        primary: "var(--glass-white)",
        secondary: "var(--glass-gray-200)",
        accent: "var(--glass-gray-100)",
      },
      rainbow: { primary: "#FF0080", secondary: "#00FF80", accent: "#8000FF" },
    };

    const colors = colorSchemes[colorScheme];

    // Animation loop
    useEffect(() => {
      const animate = () => {
        setAnimationTime((prev: any) => prev + 0.016); // ~60fps

        if (autoRotate) {
          setCurrentProjection((prev: any) => ({
            ...prev,
            angle: (prev.angle + rotationSpeed * 0.5) % 360,
          }));
        }

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      if (!prefersReducedMotion && !loading) {
        animate();
      }

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [prefersReducedMotion, loading, autoRotate, rotationSpeed]);

    // Handle mouse movement for interactive projection
    const handleMouseMove = useCallback(
      (event: React.MouseEvent) => {
        if (!interactive || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

        setMousePosition({ x, y });

        if (interactive) {
          const newProjection = {
            ...currentProjection,
            angle: currentProjection.angle + x * 10,
            tilt: currentProjection.tilt + y * 5,
          };

          setCurrentProjection(newProjection);
          onProjectionChange?.(newProjection);
        }
      },
      [interactive, currentProjection, onProjectionChange]
    );

    // Calculate layer transform
    const getLayerTransform = useCallback(
      (layer: HologramLayer) => {
        let transform = "";

        // Base positioning
        const { x, y, z } = layer.position;
        const { x: scaleX, y: scaleY, z: scaleZ } = layer.scale;
        const { x: rotX, y: rotY, z: rotZ } = layer.rotation;

        // Animation transforms
        let animX = x,
          animY = y,
          animZ = z;
        let animRotX = rotX,
          animRotY = rotY,
          animRotZ = rotZ;
        let animScale = 1;

        if (layer.animation?.enabled) {
          const { type, speed, amplitude } = layer.animation;
          const time = animationTime * speed;

          switch (type) {
            case "float":
              animY += Math.sin(time) * amplitude;
              break;
            case "rotate":
              animRotY += time * 50;
              break;
            case "pulse":
              animScale = 1 + Math.sin(time) * amplitude * 0.2;
              break;
            case "wave":
              animX += Math.sin(time) * amplitude;
              animZ += Math.cos(time) * amplitude;
              break;
            case "spiral":
              animX += Math.sin(time) * amplitude;
              animY += Math.cos(time) * amplitude;
              animRotZ += time * 30;
              break;
          }
        }

        // Projection-based transforms
        const perspective = 800;
        const projectionScale = perspective / (perspective + layer.depth);

        transform += `perspective(${perspective}px) `;
        transform += `translate3d(${animX}px, ${animY}px, ${animZ}px) `;
        transform += `rotateX(${animRotX + currentProjection.tilt}deg) `;
        transform += `rotateY(${animRotY + currentProjection.angle}deg) `;
        transform += `rotateZ(${animRotZ}deg) `;
        transform += `scale3d(${scaleX * animScale * projectionScale}, ${scaleY * animScale * projectionScale}, ${scaleZ * animScale}) `;

        return transform;
      },
      [animationTime, currentProjection]
    );

    // Calculate layer style
    const getLayerStyle = useCallback(
      (layer: HologramLayer) => {
        const style: React.CSSProperties = {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: getLayerTransform(layer),
          transformOrigin: "center center",
          opacity: layer.opacity,
          zIndex: Math.round(100 - layer.depth),
          pointerEvents: selectedLayer === layer.id ? "auto" : "none",
        };

        // Holographic effects
        if (layer.holographic?.chromatic && chromaticAberration) {
          style.filter = `drop-shadow(2px 0 0 ${colors.primary}40) drop-shadow(-2px 0 0 ${colors.secondary}40)`;
        }

        if (layer.holographic?.interference) {
          style.background = `linear-gradient(90deg, transparent 49%, ${colors.accent}20 50%, transparent 51%)`;
          style.backgroundSize = "2px 100%";
          style.animation = !prefersReducedMotion
            ? "interference 2s linear infinite"
            : undefined;
        }

        // Glitch effects
        if (layer.glitch?.enabled && glitchEnabled) {
          const glitchIntensity = layer.glitch.intensity;
          const offset = Math.random() * glitchIntensity;
          style.transform += ` translateX(${offset}px)`;

          if (Math.random() < layer.glitch.frequency) {
            style.filter =
              (style.filter || "") + ` hue-rotate(${Math.random() * 360}deg)`;
          }
        }

        return style;
      },
      [
        getLayerTransform,
        selectedLayer,
        colors,
        chromaticAberration,
        glitchEnabled,
        prefersReducedMotion,
      ]
    );

    // Handle layer click
    const handleLayerClick = useCallback(
      (layer: HologramLayer) => {
        setSelectedLayer(layer.id === selectedLayer ? null : layer.id);
        onLayerSelect?.(layer);
        feedback("tap");
      },
      [selectedLayer, onLayerSelect, feedback]
    );

    // Render layer content
    const renderLayerContent = useCallback(
      (layer: HologramLayer) => {
        switch (layer.type) {
          case "text":
            return (
              <div
                className={cn(
                  "text-center font-mono whitespace-nowrap",
                  "select-none pointer-events-none"
                )}
                style={{
                  color: "var(--glass-theme-text, var(--glass-text-primary))",
                  textShadow: "0 1px 0 rgba(255,255,255,0.18)",
                  fontSize: `${config.scale}rem`,
                }}
              >
                {layer.content}
              </div>
            );

          case "image":
            return (
              <img
                src={layer.source}
                alt={layer.id}
                className="glass-max-w-full glass-max-h-full glass-object-contain glass-select-none"
                style={{
                  filter: `drop-shadow(0 0 20px ${colors.primary}60)`,
                }}
                draggable={false}
              />
            );

          case "video":
            return (
              <video
                src={layer.source}
                className="glass-max-w-full glass-max-h-full glass-object-contain"
                style={{
                  filter: `drop-shadow(0 0 20px ${colors.primary}60)`,
                }}
                autoPlay
                muted
                loop
                playsInline
              />
            );

          case "shape":
            return (
              <div
                className="glass-w-16 glass-h-16 glass-border-2 glass-radius-lg"
                style={{
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}20`,
                  boxShadow: `0 0 20px ${colors.primary}60, inset 0 0 10px ${colors.primary}40`,
                }}
              />
            );

          case "particle":
            return (
              <div className="glass-relative glass-w-20 glass-h-20">
                {Array.from({ length: 8 }, (_, i) => (
                  <div
                    key={i}
                    className="glass-absolute glass-w-1 glass-h-1 glass-radius-full"
                    style={{
                      backgroundColor: colors.accent,
                      boxShadow: `0 0 4px ${colors.accent}`,
                      left: `${Math.cos((i / 8) * Math.PI * 2) * 30 + 40}px`,
                      top: `${Math.sin((i / 8) * Math.PI * 2) * 30 + 40}px`,
                      animation: !prefersReducedMotion
                        ? `particle-orbit ${2 + i * 0.2}s linear infinite`
                        : undefined,
                    }}
                  />
                ))}
              </div>
            );

          default:
            return layer.content;
        }
      },
      [colors, config.scale, prefersReducedMotion]
    );

    // Render background pattern
    const renderBackground = useCallback(() => {
      switch (background) {
        case "grid":
          return (
            <div
              className="glass-absolute glass-inset-0 glass-opacity-20"
              style={{
                backgroundImage: `
                  linear-gradient(${colors.primary}40 1px, transparent 1px),
                  linear-gradient(90deg, ${colors.primary}40 1px, transparent 1px)
                `,
                backgroundSize: "20px 20px",
              }}
            />
          );

        case "circuit":
          return (
            <div className="glass-absolute glass-inset-0 glass-opacity-10">
              <svg className="glass-w-full glass-h-full">
                {Array.from({ length: 10 }, (_, i) => (
                  <g key={i}>
                    <path
                      d={`M${i * 30} 0 L${i * 30} ${config.height} M0 ${i * 25} L${config.width} ${i * 25}`}
                      stroke={colors.primary}
                      strokeWidth="0.5"
                      fill="none"
                    />
                    <circle
                      cx={i * 30}
                      cy={i * 25}
                      r="2"
                      fill={colors.accent}
                    />
                  </g>
                ))}
              </svg>
            </div>
          );

        case "matrix":
          return (
            <div
              className="glass-absolute glass-inset-0 glass-opacity-15 glass-font-mono glass-text-xs glass-overflow-hidden"
              style={{ color: colors.primary }}
            >
              {Array.from({ length: 20 }, (_, i) => (
                <div
                  key={i}
                  className="glass-absolute glass-whitespace-nowrap glass-animate-pulse"
                  style={{
                    left: `${i * 5}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                  }}
                >
                  {Math.random().toString(36).substring(7)}
                </div>
              ))}
            </div>
          );

        case "particles":
          return (
            <div className="glass-absolute glass-inset-0">
              {Array.from({ length: 30 }, (_, i) => (
                <div
                  key={i}
                  className="glass-absolute glass-w-1 glass-h-1 glass-radius-full glass-opacity-30"
                  style={{
                    backgroundColor: colors.accent,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: !prefersReducedMotion
                      ? `float ${3 + Math.random() * 2}s ease-in-out infinite alternate`
                      : undefined,
                    animationDelay: `${Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>
          );

        default:
          return null;
      }
    }, [background, colors, config, prefersReducedMotion]);

    // Render scan lines overlay
    const renderScanLines = () => {
      if (!scanLines) return null;

      return (
        <div
          className="glass-absolute glass-inset-0 glass-pointer-events-none glass-opacity-20"
          style={{
            background: `repeating-linear-gradient(0deg, transparent, transparent 2px, ${colors.primary}40 2px, ${colors.primary}40 4px)`,
            backgroundPosition: "0 0",
            animation: !prefersReducedMotion
              ? "hologram-scan-lines 2s linear infinite"
              : undefined,
          }}
        />
      );
    };

    // Render noise overlay
    const renderNoiseOverlay = () => {
      if (!noiseOverlay) return null;

      return (
        <div
          className="glass-absolute glass-inset-0 glass-pointer-events-none glass-opacity-10"
          ref={(el) => {
            if (!el) return;
            el.style.background =
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")";
            el.style.animation = !prefersReducedMotion
              ? "noise 0.2s steps(10) infinite"
              : "";
          }}
        />
      );
    };

    return (
      <OptimizedGlass
        data-glass-component
        ref={ref}
        id={hologramId}
        elevation="level1"
        intensity="medium"
        depth={1}
        tint="neutral"
        border="subtle"
        className={cn(
          "glass-hologram relative glass-radius-lg glass-backdrop-blur-md border border-border/20 glass-overflow-hidden",
          className
        )}
        style={{
          width: config.width,
          maxWidth: "100%",
          height: config.height,
          background: `radial-gradient(circle, ${colors.primary}10 0%, transparent 70%)`,
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        role="region"
        aria-label="Holographic display"
        {...props}
      >
        <Motion
          preset={
            !prefersReducedMotion && respectMotionPreference ? "fadeIn" : "none"
          }
          className="glass-relative glass-w-full glass-h-full"
        >
          {/* Background Pattern */}
          {renderBackground()}

          {/* Loading State */}
          {loading && (
            <div className="glass-absolute glass-inset-0 glass-flex glass-items-center glass-justify-center">
              <div className="glass-flex glass-flex-col glass-items-center glass-gap-4">
                <div
                  className="glass-w-12 glass-h-12 glass-border-4 glass-border-t-transparent glass-radius-full glass-animate-spin"
                  style={{
                    borderColor: colors.primary,
                    borderTopColor: "transparent",
                  }}
                />
                <div
                  className="glass-text-sm"
                  style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
                >
                  Initializing Hologram...
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="glass-absolute glass-inset-0 glass-flex glass-items-center glass-justify-center">
              <div className="glass-text-center">
                <div className="glass-text-2xl glass-mb-2">⚠️</div>
                <div
                  className="glass-text-sm"
                  style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
                >
                  {error}
                </div>
              </div>
            </div>
          )}

          {/* Hologram Container */}
          {!loading && !error && (
            <div
              ref={hologramRef}
              className="glass-relative glass-w-full glass-h-full"
              style={{
                ...{
                  transformStyle: "preserve-3d",
                  perspective: "800px",
                },
              }}
            >
              {/* Hologram Layers */}
              {layers.map((layer, index) => {
                const layerStyle = {
                  ...getLayerStyle(layer),
                  "--ring-color": colors.accent,
                } as React.CSSProperties;

                return (
                  <Motion
                    key={layer.id}
                    preset={
                      !prefersReducedMotion && respectMotionPreference
                        ? "scaleIn"
                        : "none"
                    }
                    delay={index * 100}
                  >
                    <div
                      className={cn(
                        "hologram-layer cursor-pointer transition-all duration-200",
                        selectedLayer === layer.id && "ring-2 ring-offset-2",
                        layer.holographic?.scanlines && "scanning"
                      )}
                      style={{ ...layerStyle }}
                      onClick={() => handleLayerClick(layer)}
                    >
                      {renderLayerContent(layer)}
                    </div>
                  </Motion>
                );
              })}
            </div>
          )}

          {/* Scan Lines Overlay */}
          {renderScanLines()}

          {/* Noise Overlay */}
          {renderNoiseOverlay()}

          {/* Controls */}
          {showControls && !loading && !error && (
            <div
              className="glass-absolute glass-bottom-4"
              style={{ left: "50%", transform: "translateX(-50%)" }}
            >
              <OptimizedGlass
                elevation="level3"
                intensity="strong"
                depth={2}
                tint="neutral"
                border="subtle"
                className="glass-flex glass-items-center glass-gap-2 glass-px-4 glass-py-2 glass-radius-lg glass-backdrop-blur-md glass-border glass-border-glass-border/20 glass-contrast-guard"
              >
                <button
                  onClick={() =>
                    setCurrentProjection((prev: any) => ({
                      ...prev,
                      angle: (prev.angle + 90) % 360,
                    }))
                  }
                  className="glass-p-1 glass-radius-md hover:glass-surface-overlay glass-transition-all"
                  title="Rotate"
                  style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
                >
                  🔄
                </button>

                <button
                  onClick={() =>
                    setCurrentProjection((prev: any) => ({
                      ...prev,
                      distance: prev.distance === 100 ? 150 : 100,
                    }))
                  }
                  className="glass-p-1 glass-radius-md hover:glass-surface-overlay glass-transition-all"
                  title="Zoom"
                  style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
                >
                  🔍
                </button>

                <div
                  className="glass-text-xs"
                  style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
                >
                  Layers: {layers.length}
                </div>
              </OptimizedGlass>
            </div>
          )}

          {/* Debug Info */}
          {debug && !loading && !error && (
            <OptimizedGlass
              elevation="level2"
              intensity="medium"
              depth={1}
              tint="neutral"
              border="subtle"
              className="glass-absolute glass-top-4 glass-left-4 glass-p-3 glass-radius-lg glass-backdrop-blur-md glass-border glass-border-glass-border/20 glass-contrast-guard"
            >
              <div
                className="glass-text-xs glass-font-mono glass-gap-1"
                style={{ color: "var(--glass-theme-text, var(--glass-text-primary))" }}
              >
                <div>Angle: {currentProjection.angle.toFixed(1)}°</div>
                <div>Tilt: {currentProjection.tilt.toFixed(1)}°</div>
                <div>Distance: {currentProjection.distance}</div>
                <div>Layers: {layers.length}</div>
                <div>Selected: {selectedLayer || "None"}</div>
                <div>Interactive: {interactive ? "Yes" : "No"}</div>
                <div>Auto-rotate: {autoRotate ? "Yes" : "No"}</div>
              </div>
            </OptimizedGlass>
          )}

          {/* Interaction Indicator */}
          {interactive && isHovering && (
            <div className="glass-absolute glass-top-4 glass-right-4">
              <div
                className="glass-w-3 glass-h-3 glass-radius-full glass-animate-pulse"
                style={{ backgroundColor: colors.accent }}
              />
            </div>
          )}
        </Motion>

        {/* CSS Animations */}
        <style>{`
          @keyframes interference {
            0%, 100% { background-position: 0 0; }
            50% { background-position: 100% 0; }
          }
          
          @keyframes hologram-scan-lines {
            from { background-position: 0 0; }
            to { background-position: 0 4px; }
          }

          @keyframes hologram-layer-scan {
            0%, 100% { opacity: 0.35; }
            50% { opacity: 0.8; }
          }
          
          @keyframes noise {
            0%, 100% { opacity: 0.1; }
            50% { opacity: 0.2; }
          }
          
          @keyframes particle-orbit {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes float {
            from { transform: translateY(0px); }
            to { transform: translateY(-10px); }
          }
          
          .scanning::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: ${colors.primary};
            animation: hologram-layer-scan 2s ease-in-out infinite;
          }
        `}</style>
      </OptimizedGlass>
    );
  }
);

GlassHologram.displayName = "GlassHologram";

export default GlassHologram;
